// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import supabase from '../supabaseClient';

const AuthContext = createContext();

// Local sign-out only clears this browser. Global sign-out revokes every device — avoid
// that except when the user explicitly chooses to log out everywhere.
const signOutLocal = () => supabase.auth.signOut({ scope: 'local' });
const signOutGlobal = () => supabase.auth.signOut({ scope: 'global' });

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const profileLoadedForUserIdRef = useRef(null);

  const fetchProfile = useCallback(async (userData) => {
    try {
      const { data: userRoleData, error: userRoleError } = await supabase
        .from('users')
        .select('role')
        .eq('id', userData.id)
        .single();

      if (userRoleError) throw userRoleError;

      const normalizedRole =
        typeof userRoleData.role === 'string'
          ? userRoleData.role.trim().toLowerCase()
          : userRoleData.role;

      if (normalizedRole === 'therapist') {
        const { data: therapistData } = await supabase
          .from('therapists')
          .select('*')
          .eq('user_id', userData.id)
          .single();

        if (therapistData) {
          if (therapistData.status === 'Inactive') {
            await signOutLocal();
            setUser(null);
            setProfile(null);
            profileLoadedForUserIdRef.current = null;
            throw new Error('Your account has been deactivated. Please contact administration for assistance.');
          }

          setProfile({ ...therapistData, role: 'therapist' });
          setUser((prev) => ({ ...prev, role: 'therapist' }));
          profileLoadedForUserIdRef.current = userData.id;
          return;
        }
      } else if (normalizedRole === 'owner') {
        setProfile({ ...userData, role: 'owner' });
        setUser((prev) => ({ ...prev, role: 'owner' }));
        profileLoadedForUserIdRef.current = userData.id;
        return;
      } else if (normalizedRole === 'receptionist') {
        setProfile({ ...userData, role: 'receptionist' });
        setUser((prev) => ({ ...prev, role: 'receptionist' }));
        profileLoadedForUserIdRef.current = userData.id;
        return;
      } else if (normalizedRole === 'parent') {
        setProfile({ ...userData, role: 'parent' });
        setUser((prev) => ({ ...prev, role: 'parent' }));
        profileLoadedForUserIdRef.current = userData.id;
        return;
      }

      setProfile(null);
      profileLoadedForUserIdRef.current = null;
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfile(null);
      profileLoadedForUserIdRef.current = null;
    }
  }, []);

  const mergeSessionUser = useCallback((sessionUser) => {
    setUser((prev) => {
      if (!sessionUser) return null;
      if (prev?.id === sessionUser.id && prev?.role) {
        return { ...sessionUser, role: prev.role };
      }
      return sessionUser;
    });
  }, []);

  const tryRecoverSession = useCallback(async () => {
    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();

    if (!sessionError && session?.user) {
      mergeSessionUser(session.user);
      return session;
    }

    const {
      data: { session: refreshedSession },
      error: refreshError,
    } = await supabase.auth.refreshSession();

    if (!refreshError && refreshedSession?.user) {
      mergeSessionUser(refreshedSession.user);
      if (profileLoadedForUserIdRef.current !== refreshedSession.user.id) {
        await fetchProfile(refreshedSession.user);
      }
      return refreshedSession;
    }

    return null;
  }, [fetchProfile, mergeSessionUser]);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error) {
          throw error;
        }

        if (session?.user) {
          setUser(session.user);
          await fetchProfile(session.user);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // Avoid async work inside this callback — awaiting Supabase here can deadlock with auth internals.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'INITIAL_SESSION') {
        return;
      }

      if (session?.user) {
        mergeSessionUser(session.user);

        // Profile data rarely changes — don't refetch on every hourly token refresh.
        if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
          queueMicrotask(() => {
            void fetchProfile(session.user);
          });
        }
        return;
      }

      if (event === 'SIGNED_OUT') {
        queueMicrotask(async () => {
          const recovered = await tryRecoverSession();
          if (!recovered?.user) {
            setUser(null);
            setProfile(null);
            profileLoadedForUserIdRef.current = null;
          }
        });
      }
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      queueMicrotask(() => {
        void tryRecoverSession();
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      subscription?.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchProfile, mergeSessionUser, tryRecoverSession]);

  const login = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('role')
        .eq('id', data.user.id)
        .single();

      if (userError) throw userError;

      if (userData.role === 'therapist') {
        const { data: therapistData, error: therapistError } = await supabase
          .from('therapists')
          .select('status')
          .eq('user_id', data.user.id)
          .single();

        if (therapistError) {
          throw new Error('Unable to verify therapist status');
        }

        if (therapistData.status === 'Inactive') {
          await signOutLocal();
          throw new Error('Your account has been deactivated. Please contact administration for assistance.');
        }
      }

      setUser(data.user);
      await fetchProfile(data.user);

      return { user: { ...data.user, role: userData.role }, error: null };
    } catch (error) {
      console.error('Login error:', error);
      return { user: null, error: error.message };
    }
  };

  const logout = async (options = {}) => {
    const scope = options.everywhere ? 'global' : 'local';

    try {
      const { error } =
        scope === 'global'
          ? await signOutGlobal()
          : await signOutLocal();

      if (error) throw error;
      setUser(null);
      setProfile(null);
      profileLoadedForUserIdRef.current = null;
    } catch (error) {
      console.error('Error logging out:', error.message);
    }
  };

  const value = {
    user,
    profile,
    login,
    logout,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
